/** Truck qo'shish / tahrirlash (owner va filial menejeri -- "carriers_edit"). Driver -- shu carrierga ishga olinganlardan (backend tekshiradi). */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { US_STATES } from "@/features/drivers/types";
import { useHiredAtCarrier, useSaveTruck } from "@/features/carriers/api";
import { CAB_TYPES, TRAILER_TYPES, TRANSMISSIONS, TRUCK_STATUSES, type Truck, type TruckCrew, type TruckStatus, type TruckWrite } from "@/features/carriers/types";
import { Button } from "@/components/ui/button";
import { Field, Input, NumberInput, Select, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carrierId: number;
  truck?: Truck | null;
}

type FormState = {
  unit_number: string;
  status: TruckStatus;
  make: string;
  model: string;
  year: string;
  cab_type: string;
  transmission: string;
  trailer_type: string;
  trailer_length: string;
  vin: string;
  plate_number: string;
  plate_state: string;
  insurance_expires: string;
  inspection_expires: string;
  city: string;
  state: string;
  crew: TruckCrew;
  driver: string;
  co_driver: string;
  notes: string;
};

const EMPTY: FormState = {
  unit_number: "", status: "available", make: "", model: "", year: "", cab_type: "", transmission: "", trailer_type: "", trailer_length: "",
  vin: "", plate_number: "", plate_state: "", insurance_expires: "", inspection_expires: "", city: "", state: "", crew: "solo", driver: "", co_driver: "", notes: "",
};

function fromTruck(tr: Truck): FormState {
  return {
    unit_number: tr.unit_number, status: tr.status, make: tr.make, model: tr.model, year: tr.year ? String(tr.year) : "", cab_type: tr.cab_type,
    transmission: tr.transmission, trailer_type: tr.trailer_type, trailer_length: tr.trailer_length ? String(tr.trailer_length) : "", vin: tr.vin,
    plate_number: tr.plate_number, plate_state: tr.plate_state, insurance_expires: tr.insurance_expires ?? "", inspection_expires: tr.inspection_expires ?? "",
    city: tr.city, state: tr.state, crew: tr.crew, driver: tr.driver_ref ? String(tr.driver_ref.id) : "", co_driver: tr.co_driver_ref ? String(tr.co_driver_ref.id) : "", notes: tr.notes,
  };
}

function toWrite(f: FormState): TruckWrite {
  const num = (v: string) => (v.trim() === "" ? null : Number(v));
  return {
    unit_number: f.unit_number.trim(), status: f.status, make: f.make.trim(), model: f.model.trim(), year: num(f.year),
    cab_type: f.cab_type as TruckWrite["cab_type"], transmission: f.transmission as TruckWrite["transmission"], trailer_type: f.trailer_type as TruckWrite["trailer_type"],
    trailer_length: num(f.trailer_length), vin: f.vin.trim(), plate_number: f.plate_number.trim(), plate_state: f.plate_state,
    insurance_expires: f.insurance_expires || null, inspection_expires: f.inspection_expires || null, city: f.city.trim(), state: f.state, crew: f.crew,
    driver: num(f.driver), co_driver: f.crew === "team" ? num(f.co_driver) : null, notes: f.notes,
  };
}

export function TruckFormModal({ open, onOpenChange, carrierId, truck }: Props) {
  const { t } = useTranslation("carriers");
  const { t: tc } = useTranslation();
  const editing = !!truck;
  const [form, setForm] = useState<FormState>(truck ? fromTruck(truck) : EMPTY);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const save = useSaveTruck();
  // Driver tanlovi: shu carrierga ishga olingan driverlar (foydalanuvchi doirasida)
  const hired = useHiredAtCarrier(carrierId, open);
  const drivers = hired.data?.results ?? [];

  useEffect(() => {
    if (open) {
      setForm(truck ? fromTruck(truck) : EMPTY);
      setErrors({});
    }
  }, [open, truck]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const err = (k: string) => errors[k];

  // Joriy driver ro'yxatda bo'lmasa (boshqa filial) ham tanlov yo'qolmasin
  const options = (current: Truck["driver_ref"]) => {
    const list = drivers.map((d) => ({ id: d.id, name: d.full_name }));
    if (current && !list.some((d) => d.id === current.id)) list.unshift({ id: current.id, name: current.full_name });
    return list;
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});
    try {
      const data = toWrite(form);
      await save.mutateAsync(editing && truck ? { id: truck.id, data: { ...data, version: truck.version } } : { data: { ...data, carrier: carrierId } });
      toast.success(editing ? t("trucks.saved") : t("trucks.created"));
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.isConflict) {
          toast.error(error.changedBy ? tc("toast.conflict", { who: error.changedBy }) : tc("toast.conflictAnon"));
          onOpenChange(false);
          return;
        }
        if (error.fields) setErrors(error.fields);
        else toast.error(error.message);
      } else toast.error(t("trucks.saveFailed"));
    }
  }

  const stateOptions = (
    <>
      <option value="">—</option>
      {US_STATES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t("trucks.editTitle", { unit: truck?.unit_number || `#${truck?.id}` }) : t("trucks.newTitle")}
      size="lg"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={save.isPending} onClick={(e) => submit(e as unknown as React.FormEvent)}>
            {editing ? tc("actions.saveChanges") : tc("actions.save")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold">{t("trucks.sections.basic")}</h3>
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("trucks.f.unit")} error={err("unit_number")}>
              <Input value={form.unit_number} onChange={(e) => set("unit_number", e.target.value)} placeholder="1042" autoFocus />
            </Field>
            <Field label={t("trucks.f.status")} error={err("status")} hint={form.driver ? t("trucks.f.statusHint") : undefined}>
              <Select value={form.status} onChange={(e) => set("status", e.target.value as TruckStatus)}>
                {TRUCK_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {tc(`status.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("trucks.f.year")} error={err("year")}>
              <NumberInput group={false} value={form.year} onChange={(e) => set("year", e.target.value)} placeholder="2022" />
            </Field>
            <Field label={t("trucks.f.make")} error={err("make")}>
              <Input value={form.make} onChange={(e) => set("make", e.target.value)} placeholder="Freightliner" />
            </Field>
            <Field label={t("trucks.f.model")} error={err("model")}>
              <Input value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="Cascadia" />
            </Field>
            <Field label={t("trucks.f.cab")} error={err("cab_type")}>
              <Select value={form.cab_type} onChange={(e) => set("cab_type", e.target.value)}>
                <option value="">—</option>
                {CAB_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {t(`trucks.cab.${c}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("trucks.f.transmission")} error={err("transmission")}>
              <Select value={form.transmission} onChange={(e) => set("transmission", e.target.value)}>
                <option value="">—</option>
                {TRANSMISSIONS.map((c) => (
                  <option key={c} value={c}>
                    {t(`trucks.transmission.${c}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold">{t("trucks.sections.trailer")}</h3>
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("trucks.f.trailerType")} error={err("trailer_type")} className="col-span-2">
              <Select value={form.trailer_type} onChange={(e) => set("trailer_type", e.target.value)}>
                <option value="">—</option>
                {TRAILER_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {t(`trucks.trailer.${c}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("trucks.f.trailerLength")} error={err("trailer_length")}>
              <NumberInput group={false} value={form.trailer_length} onChange={(e) => set("trailer_length", e.target.value)} placeholder="53" />
            </Field>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold">{t("trucks.sections.registration")}</h3>
          <div className="grid grid-cols-3 gap-3">
            <Field label="VIN" error={err("vin")} className="col-span-2">
              <Input value={form.vin} onChange={(e) => set("vin", e.target.value.toUpperCase())} maxLength={17} placeholder="1FUJGLDR7CLBP8834" className="font-mono" />
            </Field>
            <div />
            <Field label={t("trucks.f.plate")} error={err("plate_number")}>
              <Input value={form.plate_number} onChange={(e) => set("plate_number", e.target.value.toUpperCase())} placeholder="R123456" />
            </Field>
            <Field label={t("trucks.f.plateState")} error={err("plate_state")}>
              <Select value={form.plate_state} onChange={(e) => set("plate_state", e.target.value)}>
                {stateOptions}
              </Select>
            </Field>
            <div />
            <Field label={t("trucks.f.insurance")} error={err("insurance_expires")}>
              <Input type="date" value={form.insurance_expires} onChange={(e) => set("insurance_expires", e.target.value)} />
            </Field>
            <Field label={t("trucks.f.inspection")} error={err("inspection_expires")} hint={t("trucks.f.inspectionHint")}>
              <Input type="date" value={form.inspection_expires} onChange={(e) => set("inspection_expires", e.target.value)} />
            </Field>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold">{t("trucks.sections.location")}</h3>
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("trucks.f.city")} error={err("city")}>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Dallas" />
            </Field>
            <Field label={t("trucks.f.state")} error={err("state")}>
              <Select value={form.state} onChange={(e) => set("state", e.target.value)}>
                {stateOptions}
              </Select>
            </Field>
            <Field label={t("trucks.f.crew")} error={err("crew")}>
              <Select value={form.crew} onChange={(e) => set("crew", e.target.value as TruckCrew)}>
                <option value="solo">{t("trucks.crew.solo")}</option>
                <option value="team">{t("trucks.crew.team")}</option>
              </Select>
            </Field>
            <Field label={t("trucks.f.driver")} error={err("driver")} hint={drivers.length === 0 && !form.driver ? t("trucks.f.noHired") : undefined} className="col-span-2">
              <Select value={form.driver} onChange={(e) => set("driver", e.target.value)}>
                <option value="">{t("trucks.f.noDriver")}</option>
                {options(truck?.driver_ref ?? null).map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.name}
                  </option>
                ))}
              </Select>
            </Field>
            {form.crew === "team" && (
              <Field label={t("trucks.f.coDriver")} error={err("co_driver")} className="col-span-2">
                <Select value={form.co_driver} onChange={(e) => set("co_driver", e.target.value)}>
                  <option value="">{t("trucks.f.noDriver")}</option>
                  {options(truck?.co_driver_ref ?? null)
                    .filter((d) => String(d.id) !== form.driver)
                    .map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {d.name}
                      </option>
                    ))}
                </Select>
              </Field>
            )}
          </div>
        </section>

        <Field label={t("trucks.f.notes")} error={err("notes")}>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
